"use client";

import { useState } from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Link from "next/link";
import { Mail, Phone, MapPin, Send, MessageSquare, CheckCircle } from "lucide-react";
import styles from "./page.module.css";

// SVG Brand Icons for high-fidelity social squares matching Footer styles
const FacebookIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M9 8H7v3h2v9h3v-9h3.6l.4-3H12V6c0-.9.1-1.1 1-1.1h1.8V2H12c-2.7 0-3 1.6-3 3.8V8z"/>
  </svg>
);

const InstagramIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="2" width="20" height="20" rx="5" ry="5"/>
    <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37z"/>
    <line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/>
  </svg>
);

const LinkedinIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.779-1.75-1.75s.784-1.75 1.75-1.75 1.75.779 1.75 1.75-.784 1.75-1.75 1.75zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
  </svg>
);

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: ""
  });
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value
    }));
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    
    // Mock API delay
    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
      setFormData({
        name: "",
        email: "",
        phone: "",
        message: ""
      });
    }, 1200);
  };

  return (
    <>
      <Navbar />

      <main className={styles.contactWrapper}>
        
        {/* Full-width Dark Hero Banner */}
        <div className={styles.heroBanner}>
          <div className={`${styles.heroContainer} container`}>
            
            {/* Breadcrumbs */}
            <div className={styles.breadcrumbs}>
              <Link href="/" className={styles.breadcrumbLink}>Home</Link>
              <span className={styles.breadcrumbSeparator}>/</span>
              <span className={styles.breadcrumbActive}>Contact Us</span>
            </div>

            {/* Banner Title */}
            <h1 className={styles.bannerTitle}>Contact Us</h1>
          </div>
        </div>

        {/* Content wrapper with the page grid lines background */}
        <div className={styles.pageContentWrapper}>
          <div className="container">
            
            {/* Grid Layout: Left Column (Info), Right Column (Form) */}
            <div className={styles.pageGrid}>
              
              {/* Left Column: Contact Cards & Socials */}
              <div className={styles.infoColumn}>
                
                <span className="badge badge-purple">Get In Touch</span>
                <h2 className={styles.gridTitle}>We'd Love to Hear From You</h2>
                <p className={styles.gridDesc}>
                  Have questions about Moteligo's cloud PMS, website CMS, or booking features? Drop us a message or contact us directly. Our support team is here to assist you 24/7.
                </p>

                {/* Contact Info Cards */}
                <div className={styles.infoCardsList}>
                  
                  {/* Address Card */}
                  <div className={styles.infoCard}>
                    <div className={styles.infoIconBox}>
                      <MapPin size={20} className={styles.cardIcon} />
                    </div>
                    <div className={styles.infoContent}>
                      <h4 className={styles.cardLabel}>Office Address</h4>
                      <p className={styles.cardText}>
                        Office A-125, Sector 16A, Noida, <br />
                        Uttar Pradesh, India
                      </p>
                    </div>
                  </div>

                  {/* Phone Card */}
                  <div className={styles.infoCard}>
                    <div className={styles.infoIconBox}>
                      <Phone size={20} className={styles.cardIcon} />
                    </div>
                    <div className={styles.infoContent}>
                      <h4 className={styles.cardLabel}>Phone Number</h4>
                      <p className={styles.cardText}>
                        <a href="tel:+919599287096" className={styles.contactLink}>
                          +91 9599287096
                        </a>
                      </p>
                    </div>
                  </div>

                  {/* Email Card */}
                  <div className={styles.infoCard}>
                    <div className={styles.infoIconBox}>
                      <Mail size={20} className={styles.cardIcon} />
                    </div>
                    <div className={styles.infoContent}>
                      <h4 className={styles.cardLabel}>Email Address</h4>
                      <p className={styles.cardText}>
                        <a href="mailto:contact@moteligo.com" className={styles.contactLink}>
                          contact@moteligo.com
                        </a>
                      </p>
                    </div>
                  </div>

                </div>

                {/* WhatsApp Action Button */}
                <div className={styles.whatsappWrapper}>
                  <a 
                    href="https://wa.me/919599287096?text=Hi,%20I'm%20interested%20in%20Moteligo%20hotel%20management%20solutions!" 
                    target="_blank" 
                    rel="noreferrer"
                    className={styles.whatsappBtn}
                    id="contact-page-whatsapp"
                  >
                    <MessageSquare size={16} /> Chat on WhatsApp
                  </a>
                </div>

                {/* Social Links Row */}
                <div className={styles.socialsWrapper}>
                  <h4 className={styles.socialsTitle}>Follow Us</h4>
                  <div className={styles.socialsGrid}>
                    <a href="https://linkedin.com" target="_blank" rel="noreferrer" className={styles.socialCard} aria-label="LinkedIn" id="contact-linkedin">
                      <LinkedinIcon />
                    </a>
                    <a href="https://instagram.com" target="_blank" rel="noreferrer" className={styles.socialCard} aria-label="Instagram" id="contact-instagram">
                      <InstagramIcon />
                    </a>
                    <a href="https://facebook.com" target="_blank" rel="noreferrer" className={styles.socialCard} aria-label="Facebook" id="contact-facebook">
                      <FacebookIcon />
                    </a>
                  </div>
                </div>

              </div>

              {/* Right Column: Contact Form */}
              <div className={styles.formColumn}>
                
                <div className={styles.formCard}>
                  
                  {submitted ? (
                    <div className={styles.successWrapper}>
                      <CheckCircle size={48} className={styles.successIcon} />
                      <h3 className={styles.successTitle}>Message Sent!</h3>
                      <p className={styles.successText}>
                        Thank you for reaching out. A Moteligo support representative will contact you shortly.
                      </p>
                      <button 
                        onClick={() => setSubmitted(false)}
                        className={styles.btnReset}
                      >
                        Send Another Message
                      </button>
                    </div>
                  ) : (
                    <form onSubmit={handleFormSubmit} className={styles.contactForm}>
                      <h3 className={styles.formTitle}>Send Us a Message</h3>
                      
                      <div className={styles.inputGroup}>
                        <label htmlFor="name" className={styles.inputLabel}>Full Name</label>
                        <input 
                          type="text" 
                          id="name"
                          name="name" 
                          required
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="John Doe"
                          className={styles.textInput}
                        />
                      </div>

                      <div className={styles.inputGroup}>
                        <label htmlFor="email" className={styles.inputLabel}>Email Address</label>
                        <input 
                          type="email" 
                          id="email"
                          name="email" 
                          required
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="john@example.com"
                          className={styles.textInput}
                        />
                      </div>

                      <div className={styles.inputGroup}>
                        <label htmlFor="phone" className={styles.inputLabel}>Phone Number</label>
                        <input 
                          type="tel" 
                          id="phone"
                          name="phone" 
                          required
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="+91 95992 87096"
                          className={styles.textInput}
                        />
                      </div>

                      <div className={styles.inputGroup}>
                        <label htmlFor="message" className={styles.inputLabel}>Message</label>
                        <textarea 
                          id="message"
                          name="message" 
                          required
                          rows={5}
                          value={formData.message}
                          onChange={handleInputChange}
                          placeholder="How can we help with your hotel operations?"
                          className={styles.textareaInput}
                        />
                      </div>

                      <button 
                        type="submit" 
                        disabled={loading}
                        className={styles.btnSubmit}
                        id="contact-form-submit"
                      >
                        {loading ? "Sending..." : "Send Message"} <Send size={14} style={{ marginLeft: "6px" }} />
                      </button>
                    </form>
                  )}

                </div>

              </div>

            </div>

            {/* Google Map Section */}
            <section className={styles.mapSection}>
              <h2 className={styles.mapSectionTitle}>Find Us in Noida</h2>
              <div className={styles.mapCard}>
                <div className={styles.mapWrapper}>
                  <iframe 
                    title="Moteligo Noida Office Location Map"
                    src="https://maps.google.com/maps?q=Noida%20Sector%2016A&t=&z=14&ie=UTF8&iwloc=&output=embed"
                    width="100%" 
                    height="400" 
                    style={{ border: 0 }} 
                    allowFullScreen 
                    loading="lazy" 
                    referrerPolicy="no-referrer-when-downgrade"
                  />
                </div>
              </div>
            </section>

          </div>
        </div>

      </main>

      <Footer />
    </>
  );
}
